import React, { useState } from "react";
import { CurrencyDollar, TrendDown, Plus, Users } from "@phosphor-icons/react";
import { formatCurrency } from "../utils/calculations";

interface ExpenseCardProps {
  monthlyExpenses: number;
  expenseBreakdown: any[];
  recentExpenseTransactions: any[];
  partners?: any[];
}

const ExpenseCard: React.FC<ExpenseCardProps> = ({
  monthlyExpenses,
  expenseBreakdown,
  recentExpenseTransactions,
  partners = [],
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const getExpenseBreakdown = () => {
    const breakdown: Record<string, number> = {};

    expenseBreakdown.forEach((item) => {
      breakdown[item.category] = item.amount;
    });

    return breakdown;
  };

  const expenseBreakdownData = getExpenseBreakdown();
  const topCategories = Object.entries(expenseBreakdownData)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 3);

  const getExpenseColor = (expense: number) => {
    if (expense >= 5000) return "text-red-500";
    if (expense >= 3000) return "text-orange-500";
    if (expense >= 1500) return "text-yellow-500";
    return "text-green-500";
  };

  const getExpenseMessage = (expense: number) => {
    if (expense >= 5000) return "High expense level";
    if (expense >= 3000) return "Moderate expense level";
    if (expense >= 1500) return "Low expense level";
    return "Very low expense level";
  };

  return (
    <div className="card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
        <div className="flex items-center space-x-2">
          <CurrencyDollar className="w-6 h-6 text-red-500" weight="duotone" />
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Monthly Expenses
          </h3>
        </div>
        <button
          onClick={() => setShowDetails(!showDetails)}
          className={`text-xs px-3 py-2 rounded transition-colors w-full sm:w-auto ${
            showDetails
              ? "bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 border border-red-300 dark:border-red-600/50"
              : "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
          }`}>
          {showDetails ? "Hide Details" : "Show Details"}
        </button>
      </div>

      <div className="space-y-3">
        <div>
          <p
            className={`text-2xl font-bold ${getExpenseColor(
              monthlyExpenses
            )}`}>
            {formatCurrency(monthlyExpenses)}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            {getExpenseMessage(monthlyExpenses)}
          </p>
        </div>

        {showDetails && (
          <>
            {/* Top Expense Categories */}
            {topCategories.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/30 rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-2">
                  <TrendDown
                    className="w-4 h-4 text-red-500"
                    weight="duotone"
                  />
                  <span className="text-sm font-medium text-red-700 dark:text-red-400">
                    Top Categories
                  </span>
                </div>
                <div className="text-xs text-gray-700 dark:text-gray-400 space-y-1">
                  {topCategories.map(([category, amount]) => (
                    <div
                      key={category}
                      className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <span>• {category}:</span>
                      </div>
                      <span className="font-medium">
                        {formatCurrency(amount)}
                      </span>
                    </div>
                  ))}
                  {partners &&
                    partners.length > 0 &&
                    partners.map((partner) => (
                      <div
                        key={`partner-${partner.id}`}
                        className="flex items-center justify-between">
                        <div className="flex items-center space-x-1">
                          <span>• {partner.name}'s Expenses:</span>
                          <Users
                            className="w-3 h-3 text-blue-500"
                            weight="duotone"
                          />
                        </div>
                        <span className="font-medium">
                          {formatCurrency(partner.monthlyExpenses)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </>
        )}

        {!showDetails && (
          <div className="text-xs text-gray-700 dark:text-gray-400">
            <p>{expenseBreakdown.length} categories</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseCard;
